export const useDocumentCarousel = () => {
  const fileStore = useFileStore()
  const carouselDocuments = ref<Array<CarouselImage>>([])
  const isCarouselOpen = ref(false)
  const carouselPending = ref(false)
  const currentIndex = ref(0)

  const getDocumentCarouselFiles = async (fileId: string, sourceFiles?: Array<FileData>) => {
    const sourceDocuments = sourceFiles ?? carouselDocuments.value.map((carouselDocument) => carouselDocument.meta)
    const selectedDocumentIndex = sourceDocuments.findIndex((document) => document._id === fileId)
    if (selectedDocumentIndex === -1) {
      console.error(`Document Carousel: File with id ${fileId} not found in source documents`)
      return
    }

    carouselPending.value = true
    try {
      carouselDocuments.value = await Promise.all(
        sourceDocuments.map(async (document) => {
          const src = await fileStore
            .getFileUrl(document._id, document.type === "IMAGE" ? "/no-image.png" : undefined, false)
            .catch(() => undefined)
          const thumbnailSrc =
            document.type === "IMAGE"
              ? await fileStore.getFileUrl(document._id, "/no-image.png", true).catch(() => "/no-image.png")
              : undefined

          return { src, thumbnailSrc, meta: document }
        }),
      )
      currentIndex.value = selectedDocumentIndex
    } catch (error) {
      console.error("Document Carousel: Failed to load document URLs", error)
    } finally {
      carouselPending.value = false
    }
  }

  const openDocumentCarousel = async (fileId: string, sourceFiles?: Array<FileData>) => {
    await getDocumentCarouselFiles(fileId, sourceFiles)
    isCarouselOpen.value = true
  }

  const refreshDocumentCarousel = async (fileId: string, sourceFiles?: Array<FileData>) => getDocumentCarouselFiles(fileId, sourceFiles)

  const removeDocumentFromCarousel = async (fileId: string, onRemove?: (fileId: string) => Promise<unknown> | unknown) => {
    const removedIndex = carouselDocuments.value.findIndex((image) => image.meta._id === fileId)
    if (removedIndex === -1) {
      console.error(`Document Carousel: File with id ${fileId} not found in carousel documents`)
      return
    }

    if (onRemove) {
      await onRemove(fileId)
    }

    carouselDocuments.value = carouselDocuments.value.filter((image) => image.meta._id !== fileId)

    if (carouselDocuments.value.length === 0) {
      isCarouselOpen.value = false
      currentIndex.value = 0
      return
    }

    if (removedIndex < currentIndex.value) {
      currentIndex.value = Math.max(currentIndex.value - 1, 0)
      return
    }

    if (currentIndex.value >= carouselDocuments.value.length) {
      currentIndex.value = carouselDocuments.value.length - 1
    }
  }

  const updateDocumentFromCarousel = (updatedFile: FileData, collections: Array<Ref<Array<FileData>> | Array<FileData>> = []) => {
    if (!updatedFile?._id) {
      return
    }

    const carouselDocumentIndex = carouselDocuments.value.findIndex((image) => image.meta._id === updatedFile._id)
    if (carouselDocumentIndex !== -1) {
      carouselDocuments.value[carouselDocumentIndex] = {
        ...carouselDocuments.value[carouselDocumentIndex]!,
        meta: updatedFile,
      }
    }

    for (const collection of collections) {
      const sourceFiles = isRef(collection) ? collection.value : collection
      if (!Array.isArray(sourceFiles)) {
        continue
      }

      const fileIndex = sourceFiles.findIndex((file) => file._id === updatedFile._id)
      if (fileIndex !== -1) {
        if (isRef(collection)) {
          collection.value = sourceFiles.toSpliced(fileIndex, 1, updatedFile)
        } else {
          collection.splice(fileIndex, 1, updatedFile)
        }
        return
      }
    }
  }

  return {
    carouselDocuments,
    isCarouselOpen,
    carouselPending,
    currentIndex,
    openDocumentCarousel,
    refreshDocumentCarousel,
    removeDocumentFromCarousel,
    updateDocumentFromCarousel,
  }
}
